import React, { useState, useRef, useEffect } from 'react';
import { Bot, Globe, Search, Sparkles } from 'lucide-react';
import { apiService, TagGenerationRequest, TagGenerationResponse, GeneratedDomain } from '../services/api';
import ChatMessage from '../components/ChatMessage';
import ChatInput from '../components/ChatInput';
import TagsDisplay from '../components/TagsDisplay';
import TagSelection from '../components/TagSelection';
import GeneratedDomains from '../components/GeneratedDomains';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

const DomainAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: "Hello! I'm your AI Domain Assistant. I can help you generate semantic tags from phrases to create domain portfolios.\n\nPlease enter a phrase or sentence that you'd like me to analyze for domain generation.",
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPhraseId, setCurrentPhraseId] = useState<number | null>(null);
  const [waitingForAdditionalContext, setWaitingForAdditionalContext] = useState(false);
  const [showTagSelection, setShowTagSelection] = useState(false);
  const [generatedTags, setGeneratedTags] = useState<any[]>([]);
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);
  const [generatedDomains, setGeneratedDomains] = useState<GeneratedDomain[]>([]);
  const [showGeneratedDomains, setShowGeneratedDomains] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (content: string, isUser: boolean) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const generateTags = async (phrase: string, phraseId?: number) => {
    setLoading(true);
    setError(null);
    setGeneratedTags([]);
    setShowTagSelection(false);

    try {
      const request: TagGenerationRequest = {
        prompts: [
          {
            role: 'user',
            content: phrase
          }
        ],
        ...(phraseId && { phraseId })
      };

      const response: TagGenerationResponse = await apiService.generateTags(request);
      
      // Store the phraseId for future requests
      if (response.phraseId) {
        setCurrentPhraseId(response.phraseId);
      }

      if (response.needAdditionalContext) {
        // Need more context from user
        setWaitingForAdditionalContext(true);
        addMessage(
          `${response.nextQuestion}\n\n${response.explanationOfNextQuestion}`,
          false
        );
      } else {
        // Have enough context, show tags
        setWaitingForAdditionalContext(false);
        setGeneratedTags(response.tags);
        
        if (response.tags && response.tags.length > 0) {
          addMessage(
            `Great! I've analyzed your phrase and generated ${response.tags.length} semantic tag categories. Please select at least 5 different terms from the categories below to generate domain keywords.`,
            false
          );
          setShowTagSelection(true);
        } else {
          addMessage(
            'I analyzed your phrase but couldn\'t generate enough semantic tags. Please try with a different phrase or provide more context.',
            false
          );
        }
      }
    } catch (err) {
      console.error('Error generating tags:', err);
      setError('Failed to generate tags. Please try again.');
      addMessage(
        'Sorry, I encountered an error while processing your request. Please try again.',
        false
      );
    } finally {
      setLoading(false);
    }
  };

  const generateDomains = async (selectedTerms: string[]) => {
    if (!currentPhraseId) {
      addMessage(
        'Error: No phrase ID found. Please start a new conversation.',
        false
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const request = {
        phraseId: currentPhraseId,
        prompts: [
          {
            role: 'user',
            content: selectedTerms.join(', ')
          }
        ]
      };

      const response = await apiService.generateDomains(request);
      
      if (response.result && response.result.length > 0) {
        setGeneratedDomains(response.result);
        setShowGeneratedDomains(true);
        addMessage(
          `Excellent! I've generated ${response.result.length} domain suggestions based on your selected terms. Here are the results:`,
          false
        );
      } else {
        addMessage(
          'I couldn\'t generate any domains with the selected terms. Please try with different terms or start a new conversation.',
          false
        );
      }
    } catch (err) {
      console.error('Error generating domains:', err);
      setError('Failed to generate domains. Please try again.');
      addMessage(
        'Sorry, I encountered an error while generating domains. Please try again.',
        false
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    addMessage(message, true);
    
    // Add a thinking message
    const thinkingMessage: Message = {
      id: Date.now().toString() + '-thinking',
      content: 'Analyzing your phrase and generating semantic tags...',
      isUser: false,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, thinkingMessage]);

    await generateTags(message, currentPhraseId || undefined);

    // Remove the thinking message
    setMessages(prev => prev.filter(msg => msg.id !== thinkingMessage.id));
  };

  const handleTagSelectionComplete = async (terms: string[]) => {
    setSelectedTerms(terms);
    setShowTagSelection(false);
    
    addMessage(
      `Perfect! I've selected ${terms.length} terms for domain generation. The selected terms are:\n\n${terms.join(', ')}\n\nGenerating domain suggestions based on these keywords...`,
      false
    );

    await generateDomains(terms);
  };

  const handleTagSelectionCancel = () => {
    setShowTagSelection(false);
    setGeneratedTags([]);
    addMessage(
      'Tag selection cancelled. You can try again with a different phrase or restart the conversation.',
      false
    );
  };

  const handleNewConversation = () => {
    setMessages([
      {
        id: '1',
        content: "Hello! I'm your AI Domain Assistant. I can help you generate semantic tags from phrases to create domain portfolios.\n\nPlease enter a phrase or sentence that you'd like me to analyze for domain generation.",
        isUser: false,
        timestamp: new Date()
      }
    ]);
    setGeneratedTags([]);
    setSelectedTerms([]);
    setGeneratedDomains([]);
    setCurrentPhraseId(null);
    setWaitingForAdditionalContext(false);
    setShowTagSelection(false);
    setShowGeneratedDomains(false);
    setError(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bot className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Domain Assistant</h1>
            <p className="text-gray-600">AI-powered domain generation and analysis</p>
          </div>
        </div>
        <button
          onClick={handleNewConversation}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          New Conversation
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Generated Domains</p>
              <p className="text-2xl font-bold text-gray-900">{generatedDomains.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Search className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Messages</p>
              <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">AI Assistant</p>
              <p className="text-2xl font-bold text-gray-900">Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Interface */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Bot className="h-5 w-5 mr-2" />
            AI Domain Assistant
          </h2>
          <p className="text-gray-600 mt-1">Ask me to analyze phrases and generate semantic tags for domain creation</p>
        </div>
        
        {/* Messages */}
        <div className="p-6 h-96 overflow-y-auto">
          <div className="space-y-4">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message.content}
                isUser={message.isUser}
                timestamp={message.timestamp}
              />
            ))}
            {loading && (
              <div className="flex justify-start mb-4">
                <div className="flex max-w-xs lg:max-w-md">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-500 mr-2 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-gray-100">
                    <LoadingSpinner size="sm" text="Processing..." />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <div className="p-6 border-t border-gray-200">
          <ChatInput
            onSendMessage={handleSendMessage}
            placeholder={
              waitingForAdditionalContext 
                ? "Please provide additional context..." 
                : "Enter a phrase to analyze for domain generation..."
            }
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>

      {/* Tag Selection */}
      {showTagSelection && (
        <TagSelection
          tags={generatedTags}
          onSelectionComplete={handleTagSelectionComplete}
          onCancel={handleTagSelectionCancel}
        />
      )}

      {/* Generated Domains */}
      {showGeneratedDomains && generatedDomains.length > 0 && (
        <GeneratedDomains
          domains={generatedDomains}
          selectedTerms={selectedTerms}
        />
      )}

      {/* Generated Tags Display (when not in selection mode) */}
      {generatedTags.length > 0 && !showTagSelection && !showGeneratedDomains && (
        <TagsDisplay tags={generatedTags} title="Generated Semantic Tags" />
      )}

      {/* Error Display */}
      {error && (
        <ErrorMessage message={error} onRetry={() => setError(null)} />
      )}
    </div>
  );
};

export default DomainAssistant; 